import { useState } from 'react';
import {
  Box,
  Chip,
  Tooltip,
  Typography,
  Avatar,
  Button,
  useTheme,
} from '@material-ui/core';
import PersonIcon from '@material-ui/icons/Person';
import GroupIcon from '@material-ui/icons/Group';
import CancelIcon from '@material-ui/icons/Cancel';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import type { Grant, GrantRelation } from '../../api/RebacExplorerClient';

type RelationConfig = { label: string; bg: string; text: string; border: string };

/** Converte "168h" ou "30m" em milissegundos. Retorna 0 se formato desconhecido. */
function parseDurationMs(duration: string): number {
  const hours = duration.match(/^(\d+)h$/);
  if (hours) return parseInt(hours[1], 10) * 3_600_000;
  const minutes = duration.match(/^(\d+)m$/);
  if (minutes) return parseInt(minutes[1], 10) * 60_000;
  return 0;
}

/** Retorna a data de expiração formatada (dd/MM/yyyy HH:mm) ou null se não computável. */
function computeExpiry(grantTime?: string, grantDuration?: string): string | null {
  if (!grantTime || !grantDuration) return null;
  const ms = parseDurationMs(grantDuration);
  if (!ms) return null;
  const expiry = new Date(new Date(grantTime).getTime() + ms);
  return expiry.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

function useRelationConfig(): Record<GrantRelation, RelationConfig> {
  const theme = useTheme();
  const isDark = theme.palette.type === 'dark';
  return {
    superadmin: { label: 'Superadmin', bg: isDark ? 'rgba(74,20,140,0.20)'  : '#f3e5f5', text: isDark ? '#ce93d8' : '#4a148c', border: isDark ? '#6a1b9a' : '#ce93d8' },
    owner:      { label: 'Owner',      bg: isDark ? 'rgba(230,81,0,0.15)'   : '#fff8e1', text: isDark ? '#ffcc80' : '#e65100', border: isDark ? '#e65100' : '#ffcc02' },
    admin:      { label: 'Admin',      bg: isDark ? 'rgba(183,28,28,0.15)'  : '#fce4ec', text: isDark ? '#ef9a9a' : '#b71c1c', border: isDark ? '#b71c1c' : '#ef9a9a' },
    editor:     { label: 'Editor',     bg: isDark ? 'rgba(13,71,161,0.15)'  : '#e3f2fd', text: isDark ? '#90caf9' : '#0d47a1', border: isDark ? '#1565c0' : '#90caf9' },
    viewer:     { label: 'Viewer',     bg: isDark ? 'rgba(255,255,255,0.06)': '#f5f5f5', text: isDark ? '#e0e0e0' : '#424242', border: isDark ? '#616161' : '#bdbdbd' },
    executor:   { label: 'Executor',   bg: isDark ? 'rgba(27,94,32,0.15)'   : '#e8f5e9', text: isDark ? '#a5d6a7' : '#1b5e20', border: isDark ? '#2e7d32' : '#a5d6a7' },
  };
}

const RELATION_ORDER: GrantRelation[] = ['superadmin', 'owner', 'admin', 'editor', 'executor', 'viewer'];

interface GrantsGridProps {
  grants: Grant[];
  canManage: boolean;
  canDelete: boolean;
  onRevoke: (grant: Grant) => void;
  onDelete?: () => void;
}

function displayName(user: string): string {
  // strip namespace prefix (e.g. "caramelo/guilhermerb" → "guilhermerb")
  return user.split('/').pop() ?? user;
}

const ADMIN_RELATIONS: GrantRelation[] = ['owner', 'admin', 'superadmin'];
const CHIPS_PER_SECTION = 10;

export function GrantsGrid({ grants, canManage, canDelete, onRevoke, onDelete }: GrantsGridProps) {
  const theme = useTheme();
  const isDark = theme.palette.type === 'dark';
  const RELATION_CONFIG = useRelationConfig();
  const [expandedSections, setExpandedSections] = useState<Set<GrantRelation>>(new Set());

  const toggleSection = (rel: GrantRelation) =>
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(rel)) next.delete(rel); else next.add(rel);
      return next;
    });

  // Grants que conferem can_admin e são diretos (não herdados via grupo/tenant)
  // — usados para detectar "último admin" e bloquear revogação de deadlock
  const directAdminGrants = grants.filter(
    g => ADMIN_RELATIONS.includes(g.relation) && g.origin === 'direct',
  );

  if (grants.length === 0) {
    return (
      <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
        Nenhuma permissão concedida para este recurso.
      </Typography>
    );
  }

  const byRelation = RELATION_ORDER.reduce<Record<GrantRelation, Grant[]>>(
    (acc, rel) => {
      acc[rel] = grants.filter(g => g.relation === rel);
      return acc;
    },
    {} as Record<GrantRelation, Grant[]>,
  );

  return (
    <Box style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {RELATION_ORDER.filter(rel => byRelation[rel].length > 0).map(rel => {
        const cfg = RELATION_CONFIG[rel];
        return (
          <Box key={rel}>
            <Box style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <Box
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '2px 10px',
                  borderRadius: 12,
                  background: cfg.bg,
                  border: `1px solid ${cfg.border}`,
                  color: cfg.text,
                  fontWeight: 700,
                  fontSize: 12,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                }}
              >
                {cfg.label}
              </Box>
              <Typography variant="caption" color="textSecondary">
                {byRelation[rel].length} {byRelation[rel].length === 1 ? 'membro' : 'membros'}
              </Typography>
            </Box>

            {(() => {
              const sectionGrants = byRelation[rel];
              const isExpanded = expandedSections.has(rel);
              const visible = isExpanded ? sectionGrants : sectionGrants.slice(0, CHIPS_PER_SECTION);
              const hidden = sectionGrants.length - CHIPS_PER_SECTION;
              return (
                <>
                  <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {visible.map((grant, i) => {
                      const isOwner = grant.relation === 'owner';
                      const isViaGroup = grant.origin === 'via_group';
                      const isViaTenant = grant.origin === 'via_tenant';
                      const isTemporary = Boolean(grant.grantDuration);
                      const expiryLabel = computeExpiry(grant.grantTime, grant.grantDuration);
                      const isLastAdmin =
                        ADMIN_RELATIONS.includes(grant.relation) &&
                        grant.origin === 'direct' &&
                        directAdminGrants.length === 1;
                      const canRevoke = canManage && !isOwner && !isViaGroup && !isViaTenant && !isLastAdmin;
                      const name = displayName(grant.user);

                      let tooltip: string;
                      if (isOwner) {
                        tooltip = 'Owner não pode ser removido aqui — use Transferir Ownership';
                      } else if (isViaTenant) {
                        tooltip = `Acesso herdado via papel "${grant.relation}" no tenant "${grant.tenantName ?? ''}" — não pode ser revogado aqui`;
                      } else if (isViaGroup) {
                        tooltip = `Herdado do grupo ${grant.groupName ?? grant.user} — remova o usuário do grupo para revogar`;
                      } else if (isLastAdmin) {
                        tooltip = 'Último admin direto — adicione outro admin antes de revogar para evitar deadlock';
                      } else if (isTemporary) {
                        tooltip = expiryLabel
                          ? `Acesso temporário — expira em ${expiryLabel}`
                          : `Acesso temporário (${grant.grantDuration}) — expira automaticamente`;
                      } else {
                        tooltip = `Revogar acesso ${cfg.label} de ${name}`;
                      }

                      return (
                        <Box key={i} style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
                          <Tooltip title={tooltip} placement="top">
                            <Chip
                              size="small"
                              avatar={
                                <Avatar style={{ background: 'transparent', width: 20, height: 20 }}>
                                  {isViaGroup || isViaTenant
                                    ? <GroupIcon style={{ fontSize: 14, color: cfg.text }} />
                                    : <PersonIcon style={{ fontSize: 14, color: cfg.text }} />}
                                </Avatar>
                              }
                              label={name}
                              onDelete={canRevoke ? () => onRevoke(grant) : undefined}
                              deleteIcon={
                                canRevoke
                                  ? <CancelIcon style={{ fontSize: 14 }} />
                                  : undefined
                              }
                              style={{
                                backgroundColor: cfg.bg,
                                color: cfg.text,
                                border: `1px solid ${cfg.border}`,
                                fontWeight: 500,
                                opacity: isViaGroup || isViaTenant ? 0.75 : 1,
                                fontStyle: isViaGroup || isViaTenant ? 'italic' : 'normal',
                              }}
                            />
                          </Tooltip>
                          {isTemporary && (
                            <Box style={{ display: 'inline-flex', alignItems: 'center', gap: 2, fontSize: 10, color: theme.palette.warning.main }}>
                              <AccessTimeIcon style={{ fontSize: 10 }} />
                              {expiryLabel ? `até ${expiryLabel}` : grant.grantDuration}
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                  {sectionGrants.length > CHIPS_PER_SECTION && (
                    <Button
                      size="small"
                      onClick={() => toggleSection(rel)}
                      style={{ marginTop: 6, fontSize: 11, textTransform: 'none', color: theme.palette.text.secondary, padding: '2px 6px' }}
                    >
                      {isExpanded ? 'Ver menos' : `+${hidden} mais`}
                    </Button>
                  )}
                </>
              );
            })()}
          </Box>
        );
      })}

      {canDelete && onDelete && (
        <Box style={{ marginTop: 24, paddingTop: 16, borderTop: `1px solid ${isDark ? theme.palette.error.dark : '#ffcdd2'}` }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<DeleteOutlineIcon />}
            onClick={onDelete}
            style={{ color: theme.palette.error.main, borderColor: theme.palette.error.main }}
          >
            Excluir recurso
          </Button>
          <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 4 }}>
            Apenas o owner ou superadmin pode excluir este recurso.
          </Typography>
        </Box>
      )}
    </Box>
  );
}
